/**
 * Seeded pseudo-random number generator using xoshiro128**.
 * Fast, well-distributed, and deterministic given the same seed.
 * All game randomness MUST go through this — never use Math.random().
 */

export interface RNGState {
  s0: number;
  s1: number;
  s2: number;
  s3: number;
}

export class SeededRandom {
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;

  constructor(seed: number) {
    // Initialize state using splitmix32 to expand a single seed into 4 state values
    let s = seed | 0;
    s = this.splitmix32(s);
    this.s0 = s;
    s = this.splitmix32(s);
    this.s1 = s;
    s = this.splitmix32(s);
    this.s2 = s;
    s = this.splitmix32(s);
    this.s3 = s;
  }

  private splitmix32(s: number): number {
    s = (s + 0x9e3779b9) | 0;
    let t = s ^ (s >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t = t ^ (t >>> 15);
    t = Math.imul(t, 0x735a2d97);
    t = t ^ (t >>> 15);
    return t >>> 0;
  }

  /**
   * Core generator: xoshiro128** — returns a 32-bit unsigned integer.
   */
  private nextUint32(): number {
    const result = (Math.imul(this.rotl(Math.imul(this.s1, 5), 7), 9)) >>> 0;

    const t = (this.s1 << 9) | 0;

    this.s2 ^= this.s0;
    this.s3 ^= this.s1;
    this.s1 ^= this.s2;
    this.s0 ^= this.s3;

    this.s2 ^= t;

    this.s3 = this.rotl(this.s3, 11);

    return result;
  }

  private rotl(x: number, k: number): number {
    return ((x << k) | (x >>> (32 - k))) | 0;
  }

  /** Returns a float in [0, 1). */
  next(): number {
    return this.nextUint32() / 4294967296;
  }

  /** Returns an integer in [min, max] (inclusive). */
  nextInt(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** Returns a float in [min, max). */
  nextFloat(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Returns true with the given probability (0-1). */
  nextBool(probability: number = 0.5): boolean {
    return this.next() < probability;
  }

  /** Returns a random element from the array. */
  pick<T>(array: readonly T[]): T {
    if (array.length === 0) {
      throw new Error('Cannot pick from empty array');
    }
    return array[this.nextInt(0, array.length - 1)]!;
  }

  /** Shuffles array in place (Fisher-Yates) and returns it. */
  shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      const temp = array[i]!;
      array[i] = array[j]!;
      array[j] = temp;
    }
    return array;
  }

  /** Saves the current RNG state for serialization. */
  getState(): RNGState {
    return {
      s0: this.s0,
      s1: this.s1,
      s2: this.s2,
      s3: this.s3,
    };
  }

  /** Restores RNG state from a saved snapshot. */
  setState(state: RNGState): void {
    this.s0 = state.s0;
    this.s1 = state.s1;
    this.s2 = state.s2;
    this.s3 = state.s3;
  }
}
